import { z } from 'zod';
import { HISTORY_PRESETS, historyDays, matchHistoryPreset, matchSchedulePreset, PERSONAL_FOLDERS, pathPlatform, scheduleForPreset, suggestedMaxAgeHours } from './plan-presets.js';
import { looksAbsolutePath, parsePolicySpec, policyCompleteness } from './policy.js';
const path = z.string().trim().min(1).max(4096);
const personal = z.enum(PERSONAL_FOLDERS);
const glob = z.string().trim().min(1).max(512);
const ext = z.string().trim().regex(/^\.?[A-Za-z0-9_+-]{1,32}$/);
const hhmm = z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/);
export const planChangeSchema = z
    .object({
    /** Folders on the computer, as the person names them or as a look-up returned them. */
    addFolders: z.array(path).max(20).default([]),
    removeFolders: z.array(path).max(20).default([]),
    addPersonalFolders: z.array(personal).max(6).default([]),
    removePersonalFolders: z.array(personal).max(6).default([]),
    /** Exact files or folders, exactly as a look-up (skipped_files, find_file, browse_backup) returned them. */
    skipFiles: z.array(path).max(50).default([]),
    stopSkippingFiles: z.array(path).max(50).default([]),
    skipPatterns: z.array(glob).max(20).default([]),
    stopSkippingPatterns: z.array(glob).max(20).default([]),
    skipFileTypes: z.array(ext).max(20).default([]),
    stopSkippingFileTypes: z.array(ext).max(20).default([]),
    schedule: z
        .object({
        preset: z.enum(['several-daily', 'daily', 'weekly']),
        time: hhmm.optional().describe('HH:MM for daily or weekly'),
        day: z.number().int().min(0).max(6).optional().describe('0 = Sunday … 6 = Saturday, for weekly'),
    })
        .optional(),
    /** Only ever longer: shortening history deletes old backups, and stays on the Backup plans page. */
    keepHistory: z.enum(['1m', '3m', '1y', '7y']).optional(),
    rename: z.string().trim().min(1).max(200).optional(),
})
    .strict();
/** Lines that mean something stops being backed up from now on. */
export const REDUCING_LINES = new Set(['remove_folder', 'remove_personal', 'skip_file', 'skip_pattern', 'skip_type']);
export class PlanChangeError extends Error {
    code;
    constructor(code, message) {
        super(message);
        this.code = code;
    }
}
// ───────────────────────── paths ─────────────────────────
/**
 * A backup's own path (`/C:/Users/x/a.txt`, `/UNC/server/share/a`, `/home/x/a`) in the form the computer
 * writes it (`C:\Users\x\a.txt`, `\\server\share\a`, `/home/x/a`). Plans match paths in that form.
 */
export function vpathToNative(p, style = 'posix') {
    if (style !== 'windows')
        return p;
    if (p === '/')
        return p;
    const rest = p.slice(1);
    if (rest.startsWith('UNC/'))
        return '\\\\' + rest.slice(4).replaceAll('/', '\\');
    const [drive, ...tail] = rest.split('/');
    return tail.length ? `${drive}\\${tail.join('\\')}` : `${drive}\\`;
}
/** The path style of a backup path, from its shape: `/C:/…` and `/UNC/…` are Windows, anything else is not. */
export function vpathStyle(p) {
    return /^\/[A-Za-z]:(\/|$)/.test(p) || p.startsWith('/UNC/') ? 'windows' : 'posix';
}
/**
 * A path the person or a look-up gave, in the computer's own form plus its platform. Backup paths are
 * converted; a path already in a computer's form is kept as it is.
 */
export function toPlanPath(raw) {
    const p = raw.trim();
    // `/C:/…` is a backup path; `C:\…`, `\\server`, `%VAR%` and `/home/…` are already a computer's own.
    const native = /^\/[A-Za-z]:(\/|$)/.test(p) || p.startsWith('/UNC/') ? vpathToNative(p, 'windows') : p;
    if (!looksAbsolutePath(native))
        throw new PlanChangeError('bad_path', `“${p}” is not a full path on a computer.`);
    const os = pathPlatform(native);
    return os ? { path: native, os } : { path: native };
}
/** Comparable form: Windows paths ignore case and separator style; trailing separators never matter. */
function key(p) {
    const windows = p.os === 'windows' || /^[A-Za-z]:|^\\\\|^%/.test(p.path);
    let s = windows ? p.path.replaceAll('/', '\\').toLowerCase() : p.path;
    while (s.length > 1 && /[\\/]$/.test(s) && !/^[a-z]:\\$/.test(s))
        s = s.slice(0, -1);
    return s;
}
const segments = (k) => k.split(/[\\/]+/).filter(Boolean);
/** Is `a` the same as `b`, or a folder that contains it? */
function covers(a, b) {
    const [sa, sb] = [segments(a), segments(b)];
    return sa.length <= sb.length && sa.every((s, i) => s === sb[i]);
}
/**
 * Would skipping this leave out a whole protected area rather than some files? A drive or the top of
 * the disk, the folder that holds everybody's profiles, one person's whole profile, or one of their
 * personal folders (Documents, Desktop…) — anything at those depths or above.
 */
function tooBroadToSkip(k) {
    const s = segments(k);
    if (s.length <= 1)
        return true; // C:\  /  /home  \\server
    const drive = s[0].endsWith(':');
    const below = drive ? s.slice(1) : s; // what follows the drive letter
    if (lower(below[0] ?? '') !== 'users' && lower(below[0] ?? '') !== 'home')
        return false;
    if (below.length <= 2)
        return true; // Users, Users\dana
    // Users\dana\Documents and the like; a file such as Users\dana\notes.txt is fine.
    return below.length === 3 && PERSONAL_FOLDERS.some((f) => lower(f) === lower(below[2]));
}
const MATCH_EVERYTHING = /^[*?./\\]*$/;
const normExt = (e) => e.trim().replace(/^\./, '').toLowerCase();
const lower = (s) => s.toLowerCase();
// ───────────────────────── applying ─────────────────────────
/**
 * The plan after the change, and one line per thing that actually changes. Refuses — with a sentence the
 * assistant can say — anything that would quietly protect much less than was asked for.
 */
export function applyPlanChange(current, input, opts = {}) {
    const change = planChangeSchema.parse(input);
    if (current.selection.mode === 'mobile')
        throw new PlanChangeError('phone_plan', 'The phone plan cannot be changed here.');
    const sel = structuredClone(current.selection);
    const lines = [];
    // Folders.
    for (const raw of change.removeFolders) {
        const p = toPlanPath(raw);
        const i = sel.includePaths.findIndex((x) => key(x) === key(p));
        if (i === -1)
            throw new PlanChangeError('not_in_plan', `“${p.path}” is not one of the folders this plan backs up.`);
        sel.includePaths.splice(i, 1);
        lines.push({ type: 'remove_folder', value: p.path, ...(p.os ? { os: p.os } : {}) });
    }
    for (const raw of change.addFolders) {
        const p = toPlanPath(raw);
        if (sel.includePaths.some((x) => key(x) === key(p)))
            continue;
        sel.includePaths.push(p);
        lines.push({ type: 'add_folder', value: p.path, ...(p.os ? { os: p.os } : {}) });
    }
    // Personal folders. Adding one switches a folders-only plan to per-person mode, as the plan form does.
    for (const f of change.removePersonalFolders) {
        if (sel.mode !== 'profiles' || !sel.profileFolders.includes(f))
            throw new PlanChangeError('not_in_plan', `This plan does not back up everybody's ${f}.`);
        sel.profileFolders = sel.profileFolders.filter((x) => x !== f);
        lines.push({ type: 'remove_personal', value: f });
    }
    if (sel.mode === 'profiles' && sel.profileFolders.length === 0)
        sel.mode = 'paths';
    for (const f of change.addPersonalFolders) {
        if (sel.mode === 'profiles' && sel.profileFolders.includes(f))
            continue;
        if (sel.mode !== 'profiles') {
            sel.mode = 'profiles';
            sel.profileFolders = [];
        }
        sel.profileFolders.push(f);
        lines.push({ type: 'add_personal', value: f });
    }
    // Skipping exact files and folders.
    for (const raw of change.stopSkippingFiles) {
        const p = toPlanPath(raw);
        const i = sel.excludePaths.findIndex((x) => key(x) === key(p));
        if (i === -1)
            continue;
        sel.excludePaths.splice(i, 1);
        lines.push({ type: 'unskip_file', value: p.path, ...(p.os ? { os: p.os } : {}) });
    }
    for (const raw of change.skipFiles) {
        const p = toPlanPath(raw);
        const k = key(p);
        if (tooBroadToSkip(k))
            throw new PlanChangeError('too_broad', `Skipping “${p.path}” would leave out a whole area of the computer, not just some files. Remove the folder from the plan instead, if that is what is wanted.`);
        const named = sel.includePaths.find((x) => covers(k, key(x)));
        if (named)
            throw new PlanChangeError('too_broad', `Skipping “${p.path}” would leave out the whole folder “${named.path}”. Remove that folder from the plan instead, if that is what is wanted.`);
        if (sel.excludePaths.some((x) => key(x) === k))
            continue;
        sel.excludePaths.push(p);
        lines.push({ type: 'skip_file', value: p.path, ...(p.os ? { os: p.os } : {}) });
    }
    // Patterns and file types.
    for (const g of change.stopSkippingPatterns) {
        const before = sel.excludeGlobs.length;
        sel.excludeGlobs = sel.excludeGlobs.filter((x) => lower(x) !== lower(g));
        if (sel.excludeGlobs.length !== before)
            lines.push({ type: 'unskip_pattern', value: g });
    }
    for (const g of change.skipPatterns) {
        if (MATCH_EVERYTHING.test(g) || /^\*\*[\\/]\*?$/.test(g))
            throw new PlanChangeError('too_broad', `The pattern “${g}” would match every file, so nothing would be backed up.`);
        if (sel.excludeGlobs.some((x) => lower(x) === lower(g)))
            continue;
        sel.excludeGlobs.push(g);
        lines.push({ type: 'skip_pattern', value: g });
    }
    for (const e of change.stopSkippingFileTypes.map(normExt)) {
        const before = sel.excludeExtensions.length;
        sel.excludeExtensions = sel.excludeExtensions.filter((x) => lower(x) !== e);
        if (sel.excludeExtensions.length !== before)
            lines.push({ type: 'unskip_type', value: e });
    }
    for (const e of change.skipFileTypes.map(normExt)) {
        if (sel.excludeExtensions.some((x) => lower(x) === e))
            continue;
        sel.excludeExtensions.push(e);
        lines.push({ type: 'skip_type', value: e });
    }
    let spec = { ...current, selection: sel };
    // When. The "warn after" follows the schedule when it was still at the suggestion, as on the plan form.
    if (change.schedule) {
        const schedule = scheduleForPreset(change.schedule.preset, current.schedule, { time: change.schedule.time, day: change.schedule.day });
        if (JSON.stringify(schedule) !== JSON.stringify(current.schedule)) {
            const auto = current.alerting.maxAgeHours === suggestedMaxAgeHours(current.schedule);
            spec = { ...spec, schedule, alerting: auto ? { ...current.alerting, maxAgeHours: suggestedMaxAgeHours(schedule) } : current.alerting };
            lines.push({ type: 'schedule', value: change.schedule.preset, ...(schedule.times?.[0] ? { time: schedule.times[0] } : {}), ...(schedule.kind === 'weekly' ? { day: schedule.days?.[0] } : {}) });
        }
    }
    // How long. Never shorter.
    if (change.keepHistory) {
        const retention = HISTORY_PRESETS[change.keepHistory];
        const [from, to] = [historyDays(current.retention), historyDays(retention)];
        if (to < from)
            throw new PlanChangeError('shorter_history', 'Keeping backups for less time deletes the older ones, which cannot be undone, so it is done on the Backup plans page, where the plan shows what would be lost.');
        if (matchHistoryPreset(current.retention) !== change.keepHistory) {
            spec = { ...spec, retention };
            lines.push({ type: 'keep_history', value: change.keepHistory, days: to });
        }
    }
    if (change.rename)
        lines.push({ type: 'rename', value: change.rename });
    // A copy may be exactly the original (a separate plan for some computers, to change later).
    if (!lines.length && !opts.allowUnchanged)
        throw new PlanChangeError('nothing_changes', 'That is already how the plan is; nothing would change.');
    // The result must still be a plan that backs something up.
    spec = parsePolicySpec(spec);
    const c = policyCompleteness(spec);
    if (!c.complete)
        throw new PlanChangeError('incomplete', `After that change the plan would not work: ${c.problems.join(' ')}`);
    return { spec, lines, ...(change.rename ? { rename: change.rename } : {}) };
}
// ───────────────────────── describing ─────────────────────────
/** What a plan does, in the terms of a change: what the assistant reads before it offers one. */
export function describePlan(spec) {
    const s = spec.selection;
    return {
        personalFolders: s.mode === 'profiles' ? s.profileFolders : [],
        folders: s.includePaths.map((p) => p.path),
        skipping: { files: s.excludePaths.map((p) => p.path), patterns: s.excludeGlobs, fileTypes: s.excludeExtensions },
        schedule: {
            preset: matchSchedulePreset(spec.schedule),
            kind: spec.schedule.kind,
            ...(spec.schedule.times ? { times: spec.schedule.times } : {}),
            ...(spec.schedule.days ? { days: spec.schedule.days } : {}),
            ...(spec.schedule.everyMinutes ? { everyMinutes: spec.schedule.everyMinutes } : {}),
            ...(spec.schedule.windowStart ? { between: `${spec.schedule.windowStart}–${spec.schedule.windowEnd}` } : {}),
        },
        keepsHistoryDays: historyDays(spec.retention),
        historyPreset: matchHistoryPreset(spec.retention),
        phonePlan: s.mode === 'mobile' || undefined,
    };
}
/** What moving from one plan to another adds and removes, for the "move computers" card. */
export function planDifference(from, to) {
    const [a, b] = [from ? describePlan(from) : null, describePlan(to)];
    const diff = (x, y) => x.filter((v) => !y.some((w) => lower(w) === lower(v)));
    const was = (k) => (a ? a[k] : []);
    return {
        gains: [...diff(b.personalFolders, was('personalFolders')), ...diff(b.folders, was('folders'))],
        loses: a ? [...diff(a.personalFolders, b.personalFolders), ...diff(a.folders, b.folders)] : [],
        historyDays: { from: a?.keepsHistoryDays ?? null, to: b.keepsHistoryDays },
    };
}
//# sourceMappingURL=plan-change.js.map